import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'ghost'
type ButtonSize = 'md' | 'lg'

interface ButtonStyleOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

/** Pills, never rectangles. Primary feels pressed from metal; ghost is translucent paper. */
export const buttonClasses = ({ variant = 'primary', size = 'md', className }: ButtonStyleOptions = {}): string => {
    return cn(
        'group/button relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-full border text-[14px] font-medium tracking-[-0.01em]',
        'transition-opacity duration-200 hover:opacity-85 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-55',
        variant === 'primary' && [
            'border-ink bg-ink text-paper',
            'bg-[linear-gradient(rgb(255_255_255/0.055),rgb(0_0_0/0.03))]',
            'shadow-[0_0_0_1px_rgb(18_18_18/0.22),0_1px_2px_rgb(18_18_18/0.13),0_2px_8px_rgb(18_18_18/0.07),inset_0_0_0_1px_rgb(255_255_255/0.08),inset_0_1px_0_rgb(255_255_255/0.16)]'
        ],
        variant === 'ghost' && [
            'border-ink/25 bg-paper/55 text-ink backdrop-blur-[10px]',
            'bg-[linear-gradient(rgb(255_255_255/0.5),rgb(255_255_255/0))]',
            'shadow-[0_1px_2px_rgb(18_18_18/0.06),0_2px_6px_rgb(18_18_18/0.04),inset_0_1px_0_rgb(255_255_255/0.75)]'
        ],
        size === 'md' && 'h-9 px-4',
        size === 'lg' && 'h-12 px-[22px]',
        className
    )
}

const ButtonArrow: FC = (): ReactNode => {
    return (
        <ArrowRight
            aria-hidden='true'
            strokeWidth={1.75}
            className='size-4 transition-transform duration-500 ease-soft group-hover/button:translate-x-0.5'
        />
    )
}

interface ButtonLinkProps extends ButtonStyleOptions {
    href: string
    children: ReactNode
    arrow?: boolean
}

export const ButtonLink: FC<ButtonLinkProps> = ({ href, children, arrow = false, variant, size, className }): ReactNode => {
    return (
        <Link href={href} className={buttonClasses({ variant, size, className })}>
            {children}
            {arrow && <ButtonArrow />}
        </Link>
    )
}

interface ButtonProps extends ButtonStyleOptions, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
    arrow?: boolean
}

export const Button: FC<ButtonProps> = ({ children, arrow = false, variant, size, className, type = 'button', ...rest }): ReactNode => {
    return (
        <button type={type} className={buttonClasses({ variant, size, className })} {...rest}>
            {children}
            {arrow && <ButtonArrow />}
        </button>
    )
}

interface TextLinkProps {
    href: string
    children: ReactNode
    className?: string
}

/** A quiet text link: faint ink that darkens on hover. */
export const TextLink: FC<TextLinkProps> = ({ href, children, className }): ReactNode => {
    return (
        <Link href={href} className={cn('text-ink/60 underline decoration-ink/25 underline-offset-4 transition-colors duration-300 hover:text-ink hover:decoration-ink/50', className)}>
            {children}
        </Link>
    )
}
